import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface TutorialCompletion {
  id: string;
  tutorial_id: string;
  completed_at: string;
}

export function useTutorialCompletion(tutorialId?: string) {
  const [isCompleted, setIsCompleted] = useState(false);
  const [completions, setCompletions] = useState<TutorialCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchCompletion = useCallback(async () => {
    if (!user || !tutorialId) {
      setIsCompleted(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('tutorial_completions')
        .select('*')
        .eq('user_id', user.id)
        .eq('tutorial_id', tutorialId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setIsCompleted(!!data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutorial completion:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setIsCompleted(false);
    } finally {
      setLoading(false);
    }
  }, [user, tutorialId]);

  const fetchAllCompletions = useCallback(async () => {
    if (!user) {
      setCompletions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('tutorial_completions')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (fetchError) throw fetchError;

      setCompletions(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutorial completions:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setCompletions([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (tutorialId) {
      fetchCompletion();
    } else {
      fetchAllCompletions();
    }
  }, [tutorialId, fetchCompletion, fetchAllCompletions]);

  const markComplete = async (targetTutorialId?: string) => {
    const idToMark = targetTutorialId || tutorialId;

    if (!user) {
      throw new Error('Must be logged in to mark tutorials as complete');
    }

    if (!idToMark) {
      throw new Error('Tutorial ID is required');
    }

    try {
      setError(null);

      setIsCompleted(true);

      const { error: insertError } = await supabase
        .from('tutorial_completions')
        .insert({
          user_id: user.id,
          tutorial_id: idToMark,
        });

      if (insertError) {
        setIsCompleted(false);
        throw insertError;
      }

      if (tutorialId) {
        await fetchCompletion();
      } else {
        await fetchAllCompletions();
      }

      return true;
    } catch (err) {
      console.error('Error marking tutorial as complete:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  const markIncomplete = async (targetTutorialId?: string) => {
    const idToUnmark = targetTutorialId || tutorialId;

    if (!user) {
      throw new Error('Must be logged in to unmark tutorials');
    }

    if (!idToUnmark) {
      throw new Error('Tutorial ID is required');
    }

    try {
      setError(null);

      setIsCompleted(false);

      const { error: deleteError } = await supabase
        .from('tutorial_completions')
        .delete()
        .eq('user_id', user.id)
        .eq('tutorial_id', idToUnmark);

      if (deleteError) {
        setIsCompleted(true);
        throw deleteError;
      }

      if (tutorialId) {
        await fetchCompletion();
      } else {
        await fetchAllCompletions();
      }

      return true;
    } catch (err) {
      console.error('Error unmarking tutorial:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  const toggleComplete = async (targetTutorialId?: string) => {
    if (isCompleted) {
      return await markIncomplete(targetTutorialId);
    } else {
      return await markComplete(targetTutorialId);
    }
  };

  const getCompletionStatus = useCallback((checkTutorialId: string): boolean => {
    return completions.some(c => c.tutorial_id === checkTutorialId);
  }, [completions]);

  return {
    isCompleted,
    completions,
    loading,
    error,
    markComplete,
    markIncomplete,
    toggleComplete,
    getCompletionStatus,
    refetch: tutorialId ? fetchCompletion : fetchAllCompletions,
  };
}
